import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Box, MessageSquare, Star, Settings, Plus, Trash2, RefreshCw, Inbox, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { CATEGORIES } from "../../data/site";
import { toast, Toaster } from "sonner";

const TABS = [
    { id: "overview", label: "Overview", icon: Settings },
    { id: "products", label: "Products", icon: Box },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "inquiries", label: "Inquiries", icon: Inbox },
];

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const [tab, setTab] = useState("overview");
    const [stats, setStats] = useState({});
    const [products, setProducts] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [inquiries, setInquiries] = useState([]);

    const load = async () => {
        const [s, p, r, i] = await Promise.all([
            api.get("/admin/stats"),
            api.get("/products"),
            api.get("/reviews"),
            api.get("/admin/inquiries"),
        ]);
        setStats(s.data); setProducts(p.data); setReviews(r.data); setInquiries(i.data);
    };

    useEffect(() => { load(); }, []);

    return (
        <div className="min-h-[calc(100vh-4rem)] bg-slate-50">
            <Toaster position="top-right" />
            <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10 grid lg:grid-cols-12 gap-6">
                {/* Sidebar */}
                <aside className="lg:col-span-3">
                    <div className="bg-white rounded-3xl border border-slate-100 premium-shadow p-5 sticky top-24">
                        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                            <div className="h-10 w-10 rounded-xl bg-slate-900 text-white grid place-items-center font-display font-bold">EG</div>
                            <div className="min-w-0">
                                <div className="font-display font-bold text-slate-900 text-sm truncate">{user?.name || "Admin"}</div>
                                <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                            </div>
                        </div>
                        <nav className="mt-4 space-y-1">
                            {TABS.map((t) => {
                                const Icon = t.icon;
                                return (
                                    <button
                                        key={t.id}
                                        data-testid={`admin-tab-${t.id}`}
                                        onClick={() => setTab(t.id)}
                                        className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition ${
                                            tab === t.id ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                                        }`}
                                    >
                                        <Icon className="w-4 h-4" /> {t.label}
                                    </button>
                                );
                            })}
                        </nav>
                        <div className="mt-4 pt-4 border-t border-slate-100 space-y-1">
                            <Link to="/" className="flex items-center justify-between px-3.5 py-2 rounded-xl text-sm text-slate-600 hover:bg-slate-100">
                                Back to site <ChevronRight className="w-4 h-4" />
                            </Link>
                            <button data-testid="admin-logout-btn" onClick={logout} className="w-full flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm text-red-600 hover:bg-red-50">
                                <LogOut className="w-4 h-4" /> Sign out
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Content */}
                <section className="lg:col-span-9 space-y-6">
                    <div className="flex items-center justify-between">
                        <h1 className="font-display font-bold text-2xl text-slate-900 tracking-tight capitalize">{tab}</h1>
                        <button onClick={load} className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50">
                            <RefreshCw className="w-4 h-4" /> Refresh
                        </button>
                    </div>

                    {tab === "overview" && <Overview stats={stats} inquiries={inquiries} />}
                    {tab === "products" && <ProductsManager products={products} reload={load} />}
                    {tab === "reviews" && <ReviewsManager reviews={reviews} reload={load} />}
                    {tab === "inquiries" && <InquiriesManager inquiries={inquiries} reload={load} />}
                </section>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</div>
            <div className="mt-2 font-display font-bold text-3xl text-slate-900">{value ?? "—"}</div>
        </div>
    );
}

function Overview({ stats, inquiries }) {
    return (
        <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Products" value={stats.products} />
                <Stat label="Reviews" value={stats.reviews} />
                <Stat label="Inquiries" value={stats.inquiries} />
                <Stat label="New Inquiries" value={stats.new_inquiries} />
            </div>
            <div className="bg-white rounded-3xl border border-slate-100 p-6">
                <div className="font-display font-bold text-slate-900">Recent inquiries</div>
                <div className="mt-4 divide-y divide-slate-100">
                    {inquiries.slice(0, 5).map((i) => (
                        <div key={i.id} className="py-3 flex items-center justify-between text-sm">
                            <div className="min-w-0">
                                <div className="font-semibold text-slate-900 truncate">{i.name}</div>
                                <div className="text-slate-500 text-xs truncate">{i.service || "General"} · {i.email}</div>
                            </div>
                            <div className={`text-xs px-2.5 py-1 rounded-full ${i.status === "new" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>{i.status}</div>
                        </div>
                    ))}
                    {inquiries.length === 0 && <div className="py-6 text-sm text-slate-400 text-center">No inquiries yet.</div>}
                </div>
            </div>
        </>
    );
}

function ProductsManager({ products, reload }) {
    const empty = { name: "", category: "smartphones", brand: "", price: 0, image: "", description: "", featured: false, in_stock: true, tags: [] };
    const [form, setForm] = useState(empty);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/admin/products", { ...form, price: parseFloat(form.price) || 0, tags: form.tags || [] });
            toast.success("Product added");
            setForm(empty);
            reload();
        } catch (e) { toast.error("Failed to save"); } finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!confirm("Delete this product?")) return;
        await api.delete(`/admin/products/${id}`);
        toast.success("Deleted");
        reload();
    };

    return (
        <>
            <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-100 p-6 grid sm:grid-cols-2 gap-4" data-testid="admin-product-form">
                <div className="sm:col-span-2 font-display font-bold text-slate-900">Add product</div>
                <Input label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-product-name" />
                <Input label="Brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} testid="admin-product-brand" />
                <div>
                    <span className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Category</span>
                    <select data-testid="admin-product-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-11 px-3 rounded-xl bg-white border border-slate-200">
                        {CATEGORIES.filter(c => c.id !== "all").map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                </div>
                <Input label="Price (USD)" required type="number" step="0.01" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="admin-product-price" />
                <Input className="sm:col-span-2" label="Image URL" required value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="admin-product-image" />
                <Input className="sm:col-span-2" label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="admin-product-desc" />
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured
                </label>
                <div className="sm:col-span-2 flex justify-end">
                    <button data-testid="admin-product-submit" disabled={busy} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-70">
                        <Plus className="w-4 h-4" /> Add product
                    </button>
                </div>
            </form>

            <div className="bg-white rounded-3xl border border-slate-100 p-6">
                <div className="font-display font-bold text-slate-900 mb-4">All products ({products.length})</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left text-slate-500 text-xs uppercase tracking-wider">
                            <tr><th className="py-2">Product</th><th>Category</th><th>Price</th><th>Featured</th><th></th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {products.map(p => (
                                <tr key={p.id} className="hover:bg-slate-50/60">
                                    <td className="py-3 flex items-center gap-3">
                                        <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                                        <div className="font-medium text-slate-900">{p.name}</div>
                                    </td>
                                    <td className="text-slate-600">{p.category}</td>
                                    <td className="text-slate-900 font-semibold">${p.price?.toFixed(2)}</td>
                                    <td>{p.featured ? "★" : "—"}</td>
                                    <td className="text-right">
                                        <button data-testid={`admin-delete-product-${p.id}`} onClick={() => remove(p.id)} className="h-8 w-8 rounded-lg hover:bg-red-50 text-red-600 grid place-items-center">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

function ReviewsManager({ reviews, reload }) {
    const empty = { name: "", rating: 5, content: "", avatar: "", location: "", featured: false };
    const [form, setForm] = useState(empty);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/reviews", { ...form, rating: parseInt(form.rating) });
            toast.success("Review added");
            setForm(empty); reload();
        } catch (e) { toast.error("Failed"); }
    };

    const remove = async (id) => {
        if (!confirm("Delete review?")) return;
        await api.delete(`/admin/reviews/${id}`);
        toast.success("Deleted");
        reload();
    };

    return (
        <>
            <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-100 p-6 grid sm:grid-cols-2 gap-4" data-testid="admin-review-form">
                <div className="sm:col-span-2 font-display font-bold text-slate-900">Add review</div>
                <Input label="Customer name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-review-name" />
                <Input label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="admin-review-location" />
                <Input label="Rating (1-5)" type="number" required value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} testid="admin-review-rating" />
                <Input label="Avatar URL" value={form.avatar} onChange={(v) => setForm({ ...form, avatar: v })} testid="admin-review-avatar" />
                <Input className="sm:col-span-2" label="Content" required value={form.content} onChange={(v) => setForm({ ...form, content: v })} testid="admin-review-content" />
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured
                </label>
                <div className="sm:col-span-2 flex justify-end">
                    <button data-testid="admin-review-submit" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800">
                        <Plus className="w-4 h-4" /> Add review
                    </button>
                </div>
            </form>

            <div className="bg-white rounded-3xl border border-slate-100 p-6">
                <div className="font-display font-bold text-slate-900 mb-4">All reviews ({reviews.length})</div>
                <div className="space-y-3">
                    {reviews.map(r => (
                        <div key={r.id} className="flex items-start gap-3 p-3 rounded-2xl hover:bg-slate-50/60">
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <div className="font-semibold text-slate-900">{r.name}</div>
                                    <div className="text-yellow-500 text-sm">{"★".repeat(r.rating)}</div>
                                </div>
                                <div className="text-sm text-slate-600 mt-1">{r.content}</div>
                            </div>
                            <button onClick={() => remove(r.id)} className="h-8 w-8 rounded-lg hover:bg-red-50 text-red-600 grid place-items-center" data-testid={`admin-delete-review-${r.id}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}

function InquiriesManager({ inquiries, reload }) {
    const setStatus = async (id, status) => {
        await api.patch(`/admin/inquiries/${id}`, { status });
        reload();
    };
    const remove = async (id) => {
        if (!confirm("Delete inquiry?")) return;
        await api.delete(`/admin/inquiries/${id}`);
        toast.success("Deleted"); reload();
    };
    return (
        <div className="bg-white rounded-3xl border border-slate-100 p-6">
            <div className="font-display font-bold text-slate-900 mb-4">Customer inquiries ({inquiries.length})</div>
            <div className="space-y-3">
                {inquiries.length === 0 && <div className="text-sm text-slate-400 text-center py-8">No inquiries yet.</div>}
                {inquiries.map((i) => (
                    <div key={i.id} className="rounded-2xl border border-slate-100 p-4" data-testid={`admin-inquiry-${i.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                                <div className="font-semibold text-slate-900">{i.name} <span className="text-slate-400 text-sm font-normal">· {i.phone}</span></div>
                                <div className="text-xs text-slate-500">{i.email} · {i.service || "General"} · prefers {i.preferred_contact}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <select value={i.status} onChange={(e) => setStatus(i.id, e.target.value)} className="h-9 text-xs rounded-lg border border-slate-200 px-2">
                                    <option value="new">New</option>
                                    <option value="contacted">Contacted</option>
                                    <option value="resolved">Resolved</option>
                                </select>
                                <button onClick={() => remove(i.id)} className="h-9 w-9 rounded-lg hover:bg-red-50 text-red-600 grid place-items-center">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="mt-3 text-sm text-slate-700 bg-slate-50 rounded-xl p-3">{i.message}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", required, className, testid, step }) {
    return (
        <label className={`block ${className || ""}`}>
            <span className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">{label}{required && <span className="text-red-500"> *</span>}</span>
            <input
                data-testid={testid}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                type={type}
                step={step}
                required={required}
                className="w-full h-11 px-3 rounded-xl bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition"
            />
        </label>
    );
}
